import { animated } from '#app/components/Primitives/Animated.js';
import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import { type Theme } from '#app/theme/index.js';
import {
  cloneElement,
  Fragment,
  type ReactElement,
  type UIEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css, styled } from 'styled-components';

const VerticalFadesContainer = styled.div`
  z-index: 2;
  position: relative;
  width: 100%;
  height: fit-content;
`;

const Fade = styled(animated.div)<{ fadeHeight: number }>`
  ${({ fadeHeight }) => css`
    position: absolute;
    pointer-events: none;
    height: ${fadeHeight}px;
    z-index: 1;
    width: 100%;
  `}
`;

const TopFade = styled(Fade)<{ color: keyof Theme['colors'] }>`
  ${({ color, theme }) => css`
    top: 0px;
    z-index: 2;
    background: linear-gradient(
      to bottom,
      ${theme.colors[color]},
      rgba(255, 255, 255, 0) 100%
    );
  `}
`;

const BottomFade = styled(Fade)<{ color: keyof Theme['colors'] }>`
  ${({ color, theme }) => css`
    bottom: 0px;
    z-index: 2;
    background: linear-gradient(
      to top,
      ${theme.colors[color]},
      rgba(255, 255, 255, 0) 100%
    );
  `}
`;

type AddVerticalFadesProps = {
  readonly asFragment?: boolean;
  readonly children: ReactElement;
  readonly color?: keyof Theme['colors'];
  readonly disabled?: boolean;
  readonly fadeHeight?: number;
  readonly forceFade?: {
    bottom?: boolean;
    top?: boolean;
  };
};

export const AddVerticalFades = ({
  asFragment = false,
  children,
  color = 'brandWhite',
  disabled = false,
  fadeHeight = 32,
  /*
   * Render fragment as container instead of div,
   * useful for when you want to render the fades
   * on a scrollable container with flexible height.
   * Child list component must have position: relative
   */
  forceFade = {
    bottom: false,
    top: false,
  },
  ...props
}: AddVerticalFadesProps) => {
  const ref = useRef<HTMLElement>(null);
  const [shouldFade, setShouldFade] = useState(false);
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);
  const [elementHeight, setElementHeight] = useState<number>(0);

  const onScroll = (uiEvent: UIEvent<HTMLElement>) => {
    const resolvedTarget = uiEvent.currentTarget;

    if (!resolvedTarget) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = resolvedTarget;

    if (scrollHeight - scrollTop <= clientHeight) {
      setFadeBottom(false);
    } else if (scrollTop === 0) {
      setFadeTop(false);
    } else if (!fadeTop || !fadeBottom) {
      setFadeTop(true);
      setFadeBottom(true);
    }
  };

  useEffect(() => {
    if (ResizeObserver) {
      const ro = new ResizeObserver((entries) => {
        const [element] = entries;
        if (element) {
          const contentRect = element.contentRect;
          setElementHeight(contentRect.height);
        }
      });

      if (ref.current) {
        // observe ref for height changes
        ro.observe(ref.current);
      }

      return () => {
        ro.disconnect();
      };
    } else {
      if (ref.current) {
        setElementHeight(ref.current.clientHeight);
      }

      return () => {};
    }
  }, [ref]);

  useEffect(() => {
    if (ref.current) {
      const element = ref.current;
      const isElementOverflowed = element.clientHeight < element.scrollHeight;

      if (isElementOverflowed) {
        setShouldFade(true);
        setFadeBottom(true);
      } else {
        setShouldFade(false);
        setFadeBottom(false);
      }
    }
  }, [elementHeight, ref]);

  const { playwrightTest } = useAppConfiguration();

  // Checks to see if this component AND it's consumer think we should be faded
  const shouldDisplayFades = !disabled && shouldFade && !playwrightTest;

  const shouldDisplayTopFade = (shouldDisplayFades && fadeTop) || forceFade.top;

  const shouldDisplayBottomFade =
    (shouldDisplayFades && fadeBottom) || forceFade.bottom;

  const Container = asFragment ? Fragment : VerticalFadesContainer;

  return (
    <Container {...(asFragment ? {} : props)}>
      <TopFade
        animate={{ opacity: shouldDisplayTopFade ? 1 : 0 }}
        color={color}
        fadeHeight={fadeHeight}
        initial={false}
      />
      <BottomFade
        animate={{ opacity: shouldDisplayBottomFade ? 1 : 0 }}
        color={color}
        fadeHeight={fadeHeight}
        initial={false}
      />
      {cloneElement(children, { onScroll, ref })}
    </Container>
  );
};
